<script setup>
import { computed } from 'vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
});

const { formatMessage } = useMessageFormatter();

const isDefaultScreen = computed(() => {
  return (
    props.config.isDefaultScreen &&
    ((props.config.welcomeHeading &&
      props.config.welcomeHeading.length !== 0) ||
      (props.config.welcomeTagLine && props.config.welcomeTagline.length !== 0))
  );
});
</script>

<template>
  <div
    class="rounded-t-lg flex-shrink-0 transition-[max-height] duration-300"
    :class="
      isDefaultScreen
        ? 'bg-n-slate-2 dark:bg-n-solid-1 px-5 pt-6 pb-4'
        : 'bg-n-slate-2 dark:bg-n-solid-1 p-4'
    "
  >
    <!-- Default Home View Layout -->
    <div v-if="isDefaultScreen" class="relative top-px flex flex-col w-full">
      <!-- Top Bar -->
      <div class="flex items-center justify-between w-full mb-6">
        <!-- Branded Logo Favicon -->
        <div class="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden shadow-lg bg-n-solid-2 dark:bg-n-solid-3">
          <img 
            v-if="config.logo" 
            :src="config.logo" 
            class="w-full h-full object-cover" 
            alt="Logo"
          />
          <div v-else class="w-full h-full bg-n-solid-2 dark:bg-n-solid-3 flex items-center justify-center">
            <img :src="'/favicon.png'" class="w-full h-full object-contain" alt="Favicon" />
          </div>
        </div>
        
        <!-- Grouped Team Avatars -->
        <div class="flex items-center">
          <div class="w-7 h-7 rounded-full border-2 border-n-slate-2 dark:border-n-solid-1 overflow-hidden -ml-2.5 first:ml-0 bg-n-slate-4 dark:bg-n-solid-3 flex items-center justify-center text-[10px] font-bold">👩</div>
          <div class="w-7 h-7 rounded-full border-2 border-n-slate-2 dark:border-n-solid-1 overflow-hidden -ml-2.5 first:ml-0 bg-n-slate-4 dark:bg-n-solid-3 flex items-center justify-center text-[10px] font-bold">👨</div>
          <div class="w-7 h-7 rounded-full border-2 border-n-slate-2 dark:border-n-solid-1 overflow-hidden -ml-2.5 first:ml-0 bg-n-slate-4 dark:bg-n-solid-3 flex items-center justify-center text-[10px] font-bold">🧑</div>
        </div>
      </div>

      <!-- Welcome Greeting -->
      <div class="mb-5">
        <h1 class="text-xl font-extrabold text-n-slate-11 dark:text-n-slate-11 leading-tight mb-0.5">
          {{ config.welcomeHeading || 'Hello there.' }}
        </h1>
        <h2 class="text-xl font-extrabold text-n-slate-12 dark:text-white leading-tight">
          {{ config.welcomeTagline || 'How can we help?' }}
        </h2>
      </div>

      <!-- Ask a Question Card Mock -->
      <div class="bg-n-solid-2 dark:bg-n-solid-3 border border-n-weak rounded-xl p-3 flex items-center justify-between transition-colors select-none">
        <div class="flex flex-col gap-0.5">
          <h3 class="text-xs font-bold text-n-slate-12 dark:text-white">Ask a question</h3>
          <p class="text-[10px] text-n-slate-11">AI Agent and team can help</p>
        </div>
        
        <div class="flex items-center">
          <div class="w-5 h-5 rounded-full border-2 border-n-solid-2 dark:border-n-solid-3 overflow-hidden -ml-1.5 bg-n-slate-4 dark:bg-n-solid-3 flex items-center justify-center text-[8px]">👨</div>
          <div class="w-6 h-6 rounded-full border-2 border-n-solid-2 dark:border-n-solid-3 bg-n-slate-5 dark:bg-n-solid-2 flex items-center justify-center -ml-1.5 z-10">
            <svg class="w-3.5 h-3.5 text-n-slate-12 dark:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Active Chat Mode Header Layout -->
    <div v-else class="relative top-px flex items-center justify-start">
      <img
        v-if="config.logo"
        :src="config.logo"
        class="mr-2 rounded-full logo h-8 w-8 mb-1"
      />
      <div>
        <div class="flex items-center justify-start gap-1">
          <span class="text-base font-medium leading-3 text-n-slate-12">
            {{ config.websiteName }}
          </span>
          <div
            v-if="config.isOnline"
            class="w-2 h-2 bg-n-teal-10 rounded-full"
          />
        </div>
        <span class="mt-1 text-xs text-n-slate-11">
          {{ config.replyTime }}
        </span>
      </div>
    </div>
  </div>
</template>
